import type { CategoryKind, YearBundle } from '@budgetx/shared';

const HEADER_ROW =
  'Udgifter;Januar;;Februar;;Marts;;April;;Maj;;Juni;;Juli;;August;;September;;Oktober;;November;;December;;Akumuleret;Akumuleret;Udgifter;Diff;;';

const CELLS_PER_ROW = 31;

export function formatKr(n: number): string {
  const abs = Math.abs(Math.trunc(n));
  const grouped = abs.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const sign = n < 0 ? '-' : '';
  return `${sign}${grouped},00`;
}

function emptyCells(count: number): string[] {
  return Array(count).fill('');
}

function padRow(cells: string[]): string[] {
  if (cells.length === CELLS_PER_ROW) return cells;
  if (cells.length > CELLS_PER_ROW) {
    throw new Error(`row overflow: ${cells.length} cells`);
  }
  return [...cells, ...emptyCells(CELLS_PER_ROW - cells.length)];
}

function emptyRow(): string[] {
  return emptyCells(CELLS_PER_ROW);
}

function yearRow(year: number): string[] {
  return padRow([String(year)]);
}

function headerRow(): string[] {
  const cells = HEADER_ROW.split(';');
  if (cells.length !== CELLS_PER_ROW) {
    throw new Error(`header row has ${cells.length} cells, expected ${CELLS_PER_ROW}`);
  }
  return cells;
}

interface RowValues {
  monthBudgets: number[];
  monthActuals: (number | null)[];
}

function buildRow(nameCell: string, values: RowValues): string[] {
  const cells: string[] = new Array(CELLS_PER_ROW).fill('');
  cells[0] = nameCell;
  let akumBudget = 0;
  let akumActual = 0;
  for (let m = 0; m < 12; m++) {
    const b = values.monthBudgets[m] ?? 0;
    const a = values.monthActuals[m];
    cells[1 + m * 2] = formatKr(b);
    cells[2 + m * 2] = a === null || a === undefined ? '' : formatKr(a);
    akumBudget += b;
    akumActual += a ?? 0;
  }
  cells[25] = formatKr(akumBudget);
  cells[26] = formatKr(akumActual);
  cells[27] = nameCell;
  cells[28] = formatKr(akumBudget - akumActual);
  return cells;
}

function sumMonths(items: { budgets: number[]; actuals: (number | null)[] }[]): RowValues {
  const monthBudgets = new Array<number>(12).fill(0);
  const monthActuals = new Array<number>(12).fill(0);
  for (const it of items) {
    for (let m = 0; m < 12; m++) {
      monthBudgets[m]! += it.budgets[m] ?? 0;
      monthActuals[m]! += it.actuals[m] ?? 0;
    }
  }
  return {
    monthBudgets,
    monthActuals: monthActuals as (number | null)[],
  };
}

export function generateBudgetCsv(bundle: YearBundle, kind: CategoryKind): string {
  const rows: string[][] = [emptyRow(), yearRow(bundle.year), headerRow()];

  const categories = bundle.categories
    .filter((c) => c.kind === kind)
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order);

  const allItems: { budgets: number[]; actuals: (number | null)[] }[] = [];

  for (const cat of categories) {
    const items = cat.items.slice().sort((a, b) => a.sort_order - b.sort_order);
    for (const it of items) {
      rows.push(buildRow(it.name, { monthBudgets: it.budgets, monthActuals: it.actuals }));
      allItems.push({ budgets: it.budgets, actuals: it.actuals });
    }
    rows.push(buildRow(`I alt ${cat.name}`, sumMonths(items)));
  }

  rows.push(buildRow('I alt', sumMonths(allItems)));

  return rows.map((r) => r.join(';')).join('\r\n');
}
